'use client';

// Third Party
import * as React from 'react';

// Utils
import dayjs from '@utils/dayjs-setup';
import { routes } from '@routes';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import { labels } from '@utils/constants';

// UI
import {
  PlacesSelectAutocomplete,
  PlacesSelectAutocompleteFilters,
} from '@components/places-select-autocomplete';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Button } from '@components/button';
import { Dropdown } from '@components/dropdown';
import { Typography } from '@components/typography';
import {
  SearchDateRangePicker,
  type FocusedInputShape,
  type SearchDateRange,
} from '@components/search-date-range-picker';

export interface SerpHeroSearchFormProps {
  placeName?: string;
  placeId?: string;
  title?: string;
  subtitle?: string;
}

export const SerpHeroSearchForm: React.FC<SerpHeroSearchFormProps> = ({
  placeName,
  placeId,
  title,
  subtitle,
}) => {
  const [place, setPlace] = React.useState<
    { id: string; name: string } | undefined
  >(
    placeId && placeId.trim()
      ? { id: placeId, name: placeName || '' }
      : undefined
  );
  const [placeError, setPlaceError] = React.useState(false);
  const [checkInDate, setCheckInDate] = React.useState<string | null>(null);
  const [checkOutDate, setCheckOutDate] = React.useState<string | null>(null);
  const [dateRangePickerInputElement, setDateRangePickerInputElement] =
    React.useState<HTMLDivElement | null>(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>(null);

  const [guestCountInputElement, setGuestCountInputElement] =
    React.useState<HTMLDivElement | null>(null);
  const [guestCountFocusedInput, setGuestCountFocusedInput] =
    React.useState(false);

  const [adultCount, setAdultCount] = React.useState(2);
  const [childCount, setChildCount] = React.useState(0);

  const handleDatesChange = (range: SearchDateRange) => {
    setCheckInDate(range.checkInDate);
    setCheckOutDate(range.checkOutDate);
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!place || !place.id || !place.id.trim()) {
      setPlaceError(true);
      return;
    }

    window.location.assign(
      routes.searchShow({
        placeId: place.id,
        checkInDate: checkInDate || undefined,
        checkOutDate: checkOutDate || undefined,
        adultCount: adultCount > 0 ? adultCount : 1,
        childCount,
        infantCount: 0,
        petCount: 0,
      })
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Title and Subtitle */}
      {title && (
        <div className="mb-5">
          {/*
            Plan 12 / Round 2 Q-7: previously rendered as `<h1>` here, but
            the destination page also renders a mobile `<h1>` in
            serp-content.tsx with the same `heroTitle` — two H1 elements in
            static HTML tripping content-quality SEO scanners. Demote this
            (the desktop search-card variant) to a styled paragraph so the
            mobile rendering's H1 in serp-content.tsx remains the canonical
            single H1 on the page (mobile-first indexing aligns).
            `aria-hidden="true"` keeps the screen-reader experience tied
            to the canonical mobile-block H1 at a single landmark.
          */}
          <Typography
            component="p"
            variant="title4"
            variantM="title3"
            aria-hidden="true"
            className="text-primary-main mb-1"
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              component="p"
              variant="body2"
              className="text-gray-main"
            >
              {subtitle}
            </Typography>
          )}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-3">
        {/* Location Input */}
        <PlacesSelectAutocomplete
          initialValue={
            placeName && placeId
              ? {
                  label: extractFirstPhraseBeforeComma(placeName),
                  value: placeId || '',
                }
              : null
          }
          placesFilter={PlacesSelectAutocompleteFilters.REGIONS}
          isInvalid={placeError}
          label="Location"
          showLabel={false}
          name="place"
          placeholder="Where are you going?"
          noResultsLabel={labels.noResultsFound}
          leftContent={
            <Icon
              name={IconNames.MAGNIFYING_GLASS}
              solid={true}
              className="text-primary-main"
            />
          }
          onChange={(_name, option) => {
            setPlaceError(false);
            if (option) {
              setPlace({ name: option.label, id: option.value });
            } else {
              setPlace(undefined);
            }
          }}
        />

        {/* Check in / Check out Row */}
        <div
          className="border-[1.5px] border-primary-main rounded-full overflow-hidden flex bg-white hover:border-primary-dark transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-main"
          ref={setDateRangePickerInputElement}
        >
          <button
            type="button"
            className="flex-1 px-4 py-2 text-left flex items-center gap-3 outline-none"
            onClick={() => setDateRangePickerFocusedInput('startDate')}
            aria-label={
              checkInDate
                ? `Open check-in date picker, selected ${dayjs(checkInDate).format('MMM D, YYYY')}`
                : 'Open check-in date picker, no date selected'
            }
          >
            <Icon
              name={IconNames.CALENDAR_RANGE}
              className="text-primary-main shrink-0"
            />
            <div>
              <span className="block text-[10px] font-medium text-primary-main leading-tight">
                Check in
              </span>
              <span className="block text-sm text-primary-main">
                {checkInDate
                  ? dayjs(checkInDate).format('MMM D, YYYY')
                  : 'Add dates'}
              </span>
            </div>
          </button>
          <div className="w-px bg-gray-light my-2" />
          <button
            type="button"
            className="flex-1 px-4 py-2 text-left flex items-center gap-3 outline-none"
            onClick={() => {
              if (!checkInDate) {
                setDateRangePickerFocusedInput('startDate');
              } else {
                setDateRangePickerFocusedInput('endDate');
              }
            }}
            aria-label={
              checkOutDate
                ? `Open check-out date picker, selected ${dayjs(checkOutDate).format('MMM D, YYYY')}`
                : 'Open check-out date picker, no date selected'
            }
          >
            <Icon
              name={IconNames.CALENDAR_RANGE}
              className="text-primary-main shrink-0"
            />
            <div>
              <span className="block text-[10px] font-medium text-primary-main leading-tight">
                Check out
              </span>
              <span className="block text-sm text-primary-main">
                {checkOutDate
                  ? dayjs(checkOutDate).format('MMM D, YYYY')
                  : 'Add dates'}
              </span>
            </div>
          </button>
        </div>

        {/* Date Picker Dropdown */}
        <Dropdown
          dropdownWidth={'100%'}
          reference={dateRangePickerInputElement}
          isOpen={!!dateRangePickerFocusedInput}
          onClickAway={() => setDateRangePickerFocusedInput(null)}
        >
          <div className="px-2 overflow-x-auto">
            <SearchDateRangePicker
              focusedInput={dateRangePickerFocusedInput}
              onFocusChange={setDateRangePickerFocusedInput}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              onDatesChange={handleDatesChange}
            />
          </div>
          <div className="flex w-full justify-end pb-2 pr-2">
            <Button
              variant="text"
              onClick={() => {
                setCheckInDate(null);
                setCheckOutDate(null);
                setDateRangePickerFocusedInput('startDate');
              }}
            >
              Clear
            </Button>
          </div>
        </Dropdown>

        {/* Adults / Children Row */}
        <div
          className="border-[1.5px] border-primary-main rounded-full overflow-hidden flex bg-white hover:border-primary-dark transition-colors focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-main"
          ref={setGuestCountInputElement}
        >
          <button
            type="button"
            className="flex-1 px-4 py-2 text-left flex items-center gap-3 outline-none"
            onClick={() => setGuestCountFocusedInput(true)}
            aria-label={`Open guest selector, Adults: ${adultCount}`}
          >
            <Icon
              name={IconNames.USER}
              className="text-primary-main shrink-0"
            />
            <div className="flex-1">
              <span className="block text-[10px] font-medium text-primary-main leading-tight">
                Adults
              </span>
              <span className="block text-sm text-primary-main">
                {adultCount}
              </span>
            </div>
            <Icon name={IconNames.CHEVRON_DOWN} className="text-primary-main" />
          </button>
          <div className="w-px bg-gray-light my-2" />
          <button
            type="button"
            className="flex-1 px-4 py-2 text-left flex items-center gap-3 outline-none"
            onClick={() => setGuestCountFocusedInput(true)}
            aria-label={`Open guest selector, Children: ${childCount}`}
          >
            <Icon
              name={IconNames.USER}
              className="text-primary-main shrink-0"
            />
            <div className="flex-1">
              <span className="block text-[10px] font-medium text-primary-main leading-tight">
                Children
              </span>
              <span className="block text-sm text-primary-main">
                {childCount}
              </span>
            </div>
            <Icon name={IconNames.CHEVRON_DOWN} className="text-primary-main" />
          </button>
        </div>

        {/* Guest Count Dropdown */}
        <Dropdown
          dropdownWidth={'100%'}
          reference={guestCountInputElement}
          isOpen={guestCountFocusedInput}
          onClickAway={() => setGuestCountFocusedInput(false)}
          className="space-y-4 divide-y divide-gray-light overflow-auto p-4 text-left"
        >
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <Typography variant="subtitle2" component="p">
                Adults
              </Typography>
              <Typography
                variant="body2"
                component="p"
                className="text-gray-main"
              >
                Ages 13 or above
              </Typography>
            </div>
            <div className="flex items-center space-x-3">
              <ButtonIcon
                icon={IconNames.MINUS}
                ariaDescribedby="remove-adult"
                ariaLabel="Remove adult"
                variant="outlined"
                className="border-gray-main text-gray-main"
                onClick={() =>
                  setAdultCount((val) => (val > 1 ? val - 1 : val))
                }
              />
              <span className="w-8 text-center font-medium">{adultCount}</span>
              <ButtonIcon
                icon={IconNames.PLUS}
                ariaDescribedby="add-adult"
                ariaLabel="Add adult"
                variant="outlined"
                className="border-gray-main text-gray-main"
                onClick={() =>
                  setAdultCount((val) =>
                    val >= guestCapacity.ADULTS ? guestCapacity.ADULTS : val + 1
                  )
                }
              />
            </div>
          </div>
          <div className="flex justify-between items-center pt-4">
            <div className="flex flex-col">
              <Typography variant="subtitle2" component="p">
                Children
              </Typography>
              <Typography
                variant="body2"
                component="p"
                className="text-gray-main"
              >
                Ages 2-12
              </Typography>
            </div>
            <div className="flex items-center space-x-3">
              <ButtonIcon
                icon={IconNames.MINUS}
                ariaDescribedby="remove-child"
                ariaLabel="Remove child"
                variant="outlined"
                className="border-gray-main text-gray-main"
                onClick={() =>
                  setChildCount((val) => (val > 0 ? val - 1 : val))
                }
              />
              <span className="w-8 text-center font-medium">{childCount}</span>
              <ButtonIcon
                icon={IconNames.PLUS}
                ariaDescribedby="add-child"
                ariaLabel="Add child"
                variant="outlined"
                className="border-gray-main text-gray-main"
                onClick={() =>
                  setChildCount((val) =>
                    val >= guestCapacity.CHILDREN
                      ? guestCapacity.CHILDREN
                      : val + 1
                  )
                }
              />
            </div>
          </div>
        </Dropdown>

        {/* Search Button */}
        <Button type="submit" size="lg" isFullWidth={true}>
          Search
        </Button>
      </form>
    </div>
  );
};
